import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { listDealNotes, updateDealNote, type Deal, type DealNote } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { LogDealProgressModal } from "@/components/deals/LogDealProgressModal";
import { NoteBody, NoteEntry, NoteThread } from "@/components/deals/NoteThread";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/toast";

const NOTE_MAX = 2000;

/** Human label for a stored activity subtype (CHP-429). */
const SUBTYPE_LABELS: Record<string, string> = {
  initial_discovery_meeting: "Initial Discovery Meeting",
  follow_up_meeting: "Follow-up Meeting",
  phone_call: "Phone Call",
  email_exchange: "Email Exchange",
  cold_outreach: "Cold Outreach",
  no_response_attempted_contact: "No Response/Attempted Contact",
};

interface ActivityDetails {
  subtype?: string;
  date?: string;
  time?: string;
  attendees?: { name: string; title?: string | null }[];
  key_discussion_points?: string;
  next_steps?: string;
  details?: string;
}

/** Render the structured fields of a logged activity entry (CHP-429). */
function ActivityBody({ note }: { note: DealNote }) {
  const d = (note.activity_details ?? {}) as ActivityDetails;
  const subtypeLabel = d.subtype ? (SUBTYPE_LABELS[d.subtype] ?? d.subtype) : null;
  const attendees = (d.attendees ?? []).filter((a) => a.name);
  return (
    <div className="mt-1 space-y-1 text-sm">
      {subtypeLabel ? <p className="font-medium">{subtypeLabel}</p> : null}
      <dl className="space-y-1 text-muted-foreground">
        {d.date ? (
          <div className="flex gap-1">
            <dt className="font-medium">When:</dt>
            <dd>
              {d.date}
              {d.time ? ` at ${d.time}` : ""}
            </dd>
          </div>
        ) : null}
        {attendees.length > 0 ? (
          <div className="flex gap-1">
            <dt className="font-medium">Attendees:</dt>
            <dd>
              {attendees.map((a) => (a.title ? `${a.name} (${a.title})` : a.name)).join(", ")}
            </dd>
          </div>
        ) : null}
        {d.key_discussion_points ? (
          <div>
            <dt className="font-medium">Key discussion points:</dt>
            <dd className="whitespace-pre-wrap">{d.key_discussion_points}</dd>
          </div>
        ) : null}
        {d.next_steps ? (
          <div>
            <dt className="font-medium">Next steps:</dt>
            <dd className="whitespace-pre-wrap">{d.next_steps}</dd>
          </div>
        ) : null}
        {d.details ? (
          <div>
            <dt className="font-medium">Details:</dt>
            <dd className="whitespace-pre-wrap">{d.details}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}

/**
 * "Deal Progress" section for the BDM/PO deal detail view (CHP-429, renamed
 * from "Notes"). Lists the deal's activity log newest-first, with an
 * unhighlighted "Log deal progress" button under the title that opens the same
 * activity modal as the Actions row. Plain free-text notes (CHP-340) remain
 * editable by their author within the 5-minute window; logged activities render
 * their structured fields. The CM/RA/RM view keeps the separate DealNotesSection
 * ("Deal Owner Notes", CHP-401) — this component is BDM/PO-only.
 */
export function DealProgressSection({
  deal,
  readOnly = false,
}: {
  deal: Deal;
  /** CHP-808: when true (a non-owner viewing the master record from an insights
   * explorer) the activity log is read-only — no "Log deal progress" button,
   * no log modal. POST /deals/{id}/notes is owner/creator-only and 404s a
   * non-owner, so the affordance would be a dead-end. */
  readOnly?: boolean;
}) {
  const { accessToken } = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const isLive = deal.deal_status === "live";

  const notesQuery = useQuery({
    queryKey: ["deals", "notes", deal.id],
    queryFn: () => listDealNotes(accessToken!, deal.id),
    enabled: !!accessToken,
  });

  const [logOpen, setLogOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editDraft, setEditDraft] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["deals", "notes", deal.id] });

  const edit = useMutation({
    mutationFn: (noteId: string) =>
      updateDealNote(accessToken!, deal.id, noteId, { body: editDraft.trim() }),
    onSuccess: () => {
      setEditingId(null);
      setEditDraft("");
      setError(null);
      invalidate();
      toast({ variant: "success", description: "Note updated" });
    },
    onError: () => setError("Couldn't update the note. The edit window may have closed."),
  });

  // Newest-first (CHP-429): the API returns oldest-first history.
  const notes = [...(notesQuery.data ?? [])].reverse();

  function startEdit(note: DealNote) {
    setEditingId(note.id);
    setEditDraft(note.body);
    setError(null);
  }

  return (
    <section>
      <h2 className="mb-3 text-base font-semibold">Deal Updates</h2>

      {/* CHP-429: an unhighlighted "Log deal progress" button under the title,
          same behaviour as the highlighted Actions-row button. Live deals
          only — a closed/terminal deal's progress log is read-only history.
          CHP-808: also hidden for a non-owner viewer (readOnly). */}
      {isLive && !readOnly ? (
        <div className="mb-4">
          <Button
            type="button"
            variant="outline"
            data-testid="log-deal-progress-section"
            onClick={() => setLogOpen(true)}
          >
            Log deal progress
          </Button>
        </div>
      ) : null}

      <NoteThread
        entries={notes}
        isLoading={notesQuery.isLoading}
        loadingLabel="Loading deal progress…"
        emptyLabel="No deal progress logged yet."
        renderEntry={(note) => (
          <NoteEntry
            key={note.id}
            author={note.author_name}
            timestamp={note.created_at}
            edited={note.updated_at !== note.created_at}
            headerAction={
              // Only plain notes carry the inline edit affordance; logged
              // activities are immutable.
              note.editable && note.activity_type === null && editingId !== note.id ? (
                <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(note)}>
                  Edit
                </Button>
              ) : null
            }
          >
            {note.activity_type !== null ? (
              <ActivityBody note={note} />
            ) : editingId === note.id ? (
              <div className="mt-2 space-y-2">
                <Textarea
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  maxLength={NOTE_MAX}
                  rows={3}
                  className="w-full"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      if (!editDraft.trim()) return;
                      edit.mutate(note.id);
                    }}
                    disabled={!editDraft.trim()}
                    loading={edit.isPending}
                    loadingLabel="Saving…"
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingId(null);
                      setEditDraft("");
                    }}
                    disabled={edit.isPending}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <NoteBody>{note.body}</NoteBody>
            )}
          </NoteEntry>
        )}
      />

      {error ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <LogDealProgressModal deal={deal} open={logOpen} onOpenChange={setLogOpen} />
    </section>
  );
}
